import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/auth/authStore';
import { ErrorBox, Loading, PageTitle } from '@/components/ui';

interface Character {
  id: string;
  name: string;
  description: string | null;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  priceCoins: number;
  priceGems: number;
  idleUrl: string | null;
  climbUrl: string | null;
  fallUrl: string | null;
  winUrl: string | null;
  framesJson: Partial<Record<'idle' | 'climb' | 'fall' | 'win', string[]>> | null;
  stompPower: number;
  tint: string | null;
  isActive: boolean;
  isStarter: boolean;
}

async function req<T>(method: string, path: string, body?: unknown, isForm = false): Promise<T> {
  const token = useAuthStore.getState().token;
  const headers: Record<string, string> = { ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  if (!isForm) headers['Content-Type'] = 'application/json';
  const res = await fetch(path, {
    method, headers,
    body: isForm ? (body as FormData) : body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let d = '';
    try { const j = await res.json(); d = j?.message ?? j?.error ?? ''; } catch { /* */ }
    throw new Error(d || `HTTP ${res.status}`);
  }
  return res.json();
}

const POSES = ['idle', 'climb', 'fall', 'win'] as const;

export function CharactersPage() {
  const [items, setItems] = useState<Character[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = () =>
    req<{ items: Character[] }>('GET', '/admin/characters/')
      .then(({ items }) => setItems(items))
      .catch((e) => setErr(String(e)));

  useEffect(() => { void load(); }, []);

  if (err) return <ErrorBox text={err} />;
  if (!items) return <Loading />;

  async function createNew() {
    setCreating(true);
    try {
      await req('POST', '/admin/characters/', { name: 'New Character', rarity: 'common' });
      await load();
    } catch (e: any) { alert(e?.message); }
    setCreating(false);
  }

  return (
    <div>
      <PageTitle title="Characters" />
      <div className="card" style={{ padding: '10px 14px', marginBottom: 16, fontSize: 12, color: 'var(--ink-muted)', background: 'rgba(34,211,238,0.08)', borderColor: 'rgba(34,211,238,0.25)' }}>
        Each character is a full climber skin. Upload a base PNG per pose (idle / climb / fall / win).
        Missing poses fall back to the default climber. Add <b>extra frames</b> to a pose to make it
        animate (flipbook) in-game. <b>stompPower</b> (0.5–2.5) scales the win-effect intensity —
        knockdown distance, screen shake, confetti.
      </div>
      <button className="btn btn-primary" onClick={createNew} disabled={creating} style={{ marginBottom: 16 }}>
        + NEW CHARACTER
      </button>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {items.map((c) => <CharacterRow key={c.id} char={c} onChanged={load} />)}
      </div>
    </div>
  );
}

function CharacterRow({ char, onChanged }: { char: Character; onChanged: () => void }) {
  const [name, setName] = useState(char.name);
  const [description, setDescription] = useState(char.description ?? '');
  const [rarity, setRarity] = useState(char.rarity);
  const [priceCoins, setPriceCoins] = useState(char.priceCoins);
  const [priceGems, setPriceGems] = useState(char.priceGems);
  const [stompPower, setStompPower] = useState(char.stompPower);
  const [tint, setTint] = useState(char.tint ?? '');
  const [isActive, setIsActive] = useState(char.isActive);
  const [isStarter, setIsStarter] = useState(char.isStarter);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      await req('PATCH', `/admin/characters/${char.id}`, {
        name, description: description || null, rarity, priceCoins, priceGems,
        stompPower, tint: tint || null, isActive, isStarter,
      });
      await onChanged();
    } catch (e: any) { alert(e?.message); }
    setBusy(false);
  }
  async function del() {
    if (!confirm(`Delete "${char.name}"? Players who own it lose it.`)) return;
    setBusy(true);
    try { await req('DELETE', `/admin/characters/${char.id}`); await onChanged(); }
    catch (e: any) { alert(e?.message); }
    setBusy(false);
  }

  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {/* Left: meta fields */}
        <div style={{ flex: '1 1 260px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ fontSize: 11, color: 'var(--ink-muted)' }}>Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} />
          <label style={{ fontSize: 11, color: 'var(--ink-muted)' }}>Description</label>
          <input value={description} onChange={(e) => setDescription(e.target.value)} />
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, color: 'var(--ink-muted)' }}>Rarity</label>
              <select value={rarity} onChange={(e) => setRarity(e.target.value as any)} style={{ width: '100%' }}>
                {['common', 'rare', 'epic', 'legendary'].map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, color: 'var(--ink-muted)' }}>Tint (hex)</label>
              <input value={tint} onChange={(e) => setTint(e.target.value)} placeholder="#E63946" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, color: 'var(--ink-muted)' }}>Price coins</label>
              <input type="number" value={priceCoins} onChange={(e) => setPriceCoins(+e.target.value)} style={{ width: '100%' }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, color: 'var(--ink-muted)' }}>Price gems</label>
              <input type="number" value={priceGems} onChange={(e) => setPriceGems(+e.target.value)} style={{ width: '100%' }} />
            </div>
          </div>
          <label style={{ fontSize: 11, color: 'var(--ink-muted)' }}>Win power (stompPower) {stompPower.toFixed(1)}</label>
          <input type="range" min={0.5} max={2.5} step={0.1} value={stompPower} onChange={(e) => setStompPower(+e.target.value)} />
          <div style={{ display: 'flex', gap: 16, fontSize: 12 }}>
            <label><input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} /> active</label>
            <label><input type="checkbox" checked={isStarter} onChange={(e) => setIsStarter(e.target.checked)} /> starter (free)</label>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={save} disabled={busy}>SAVE</button>
            <button className="btn" onClick={del} disabled={busy} style={{ color: 'var(--c-red)' }}>DELETE</button>
          </div>
        </div>

        {/* Right: pose uploads */}
        <div style={{ flex: '1 1 320px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {POSES.map((pose) => (
            <PoseUpload
              key={pose}
              charId={char.id}
              pose={pose}
              url={(char as any)[`${pose}Url`]}
              frames={char.framesJson?.[pose] ?? []}
              onChanged={onChanged}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function PoseUpload({ charId, pose, url, frames, onChanged }: { charId: string; pose: string; url: string | null; frames: string[]; onChanged: () => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const frameRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  async function upload(f: File) {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      await req('POST', `/admin/characters/${charId}/pose/${pose}`, fd, true);
      await onChanged();
    } catch (e: any) { alert(e?.message); }
    setBusy(false);
  }
  async function clear() {
    setBusy(true);
    try { await req('DELETE', `/admin/characters/${charId}/pose/${pose}`); await onChanged(); }
    catch (e: any) { alert(e?.message); }
    setBusy(false);
  }
  async function addFrame(f: File) {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      await req('POST', `/admin/characters/${charId}/pose/${pose}/frame`, fd, true);
      await onChanged();
    } catch (e: any) { alert(e?.message); }
    setBusy(false);
  }
  async function removeFrame(index: number) {
    setBusy(true);
    try { await req('DELETE', `/admin/characters/${charId}/pose/${pose}/frame/${index}`); await onChanged(); }
    catch (e: any) { alert(e?.message); }
    setBusy(false);
  }

  const totalFrames = (url ? 1 : 0) + frames.length;

  return (
    <div style={{ border: '1px solid var(--hairline)', borderRadius: 8, padding: 8, textAlign: 'center' }}>
      <div style={{ fontSize: 10, color: 'var(--c-cyan)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>
        {pose}{totalFrames > 1 ? ` · ${totalFrames}f anim` : ''}
      </div>
      <div style={{ height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)', borderRadius: 6, marginBottom: 6, overflow: 'hidden' }}>
        {url ? <img src={url} alt="" style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} /> : <span style={{ fontSize: 10, color: 'var(--ink-dim)' }}>no image</span>}
      </div>
      <input type="file" accept="image/png,image/webp,image/gif" ref={fileRef} style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); e.target.value = ''; }} />
      <input type="file" accept="image/png,image/webp,image/gif" ref={frameRef} style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) void addFrame(f); e.target.value = ''; }} />
      <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
        <button className="btn" onClick={() => fileRef.current?.click()} disabled={busy} style={{ fontSize: 10, padding: '3px 8px' }}>{url ? 'REPLACE' : 'UPLOAD'}</button>
        {url && <button className="btn" onClick={clear} disabled={busy} style={{ fontSize: 10, padding: '3px 8px', color: 'var(--c-red)' }}>✕</button>}
      </div>

      {url && (
        <div style={{ marginTop: 8, borderTop: '1px solid var(--hairline)', paddingTop: 6 }}>
          <div style={{ fontSize: 9, color: 'var(--ink-dim)', marginBottom: 4 }}>EXTRA FRAMES (anim)</div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
            {frames.map((fUrl, i) => (
              <div key={fUrl} style={{ position: 'relative', width: 36, height: 36, borderRadius: 4, overflow: 'hidden', background: 'rgba(0,0,0,0.3)' }}>
                <img src={fUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                <button
                  onClick={() => removeFrame(i)}
                  disabled={busy}
                  title="remove frame"
                  style={{ position: 'absolute', top: 0, right: 0, background: 'rgba(0,0,0,0.6)', color: 'var(--c-red)', border: 'none', fontSize: 9, lineHeight: 1, cursor: 'pointer', padding: '1px 3px' }}
                >✕</button>
              </div>
            ))}
            <button className="btn" onClick={() => frameRef.current?.click()} disabled={busy} style={{ fontSize: 14, padding: 0, width: 36, height: 36 }} title="add frame">＋</button>
          </div>
        </div>
      )}
    </div>
  );
}
